import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { DataTable } from "components/dataTable/index.jsx";
import { useEffect, useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function DisneyBalance(props) {
  const appDataStore = useAppDataStore();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFormData, setReportFormData] = useState({
    photo: null,
    amount: '',
    reference: '',
    balanceType: ''
  });
  const [reportingLoading, setReportingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);
  const [photoUrl, setPhotoUrl] = useState(null);

  // Cargar balances al montar el componente
  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      setLoading(true);
      // No se envía userId, el backend usa el del usuario autenticado
      const data = await appDataStore.post("/balance/user-balances", {});
      setBalances(data.balances || []);
    } catch (err) {
      console.error("Error al cargar balances:", err);
      alert("Error al cargar saldos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Columnas para historial de recargas
  const historyColumns = useMemo(() => ([
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => `+${formatters.cash(cell.getValue())}`,
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'previousBalance',
      header: 'Saldo Anterior',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.previousBalance
    },
    {
      accessorKey: 'newBalance',
      header: 'Saldo Nuevo',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.newBalance
    },
    {
      accessorKey: 'reason',
      header: 'Detalles',
      enableColumnFilter: false,
      cell: ({cell}) => cell.getValue() || '-'
    },
    {
      accessorKey: 'admin',
      header: 'Realizado Por',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const admin = cell.getValue();
        return admin ? `${admin.name} (${admin.email})` : 'Sistema';
      }
    }
  ]), []);

  const historyDataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/BalanceRecharge`, {
      records,
      currentPage,
      order,
      search,
      filter,
      attributes: ['id', 'amount', 'reason', 'previousBalance', 'newBalance', 'createdAt'],
      include: [
        {
          model: 'User',
          as: 'admin',
          attributes: ['id', 'name', 'email']
        }
      ],
      searchAttributes: ['reason']
    });
  }, [appDataStore]);

  // Columnas para reportes de consignación
  const reportsColumns = useMemo(() => ([
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'balanceType',
      header: 'Tipo de Saldo',
    },
    {
      accessorKey: 'photo',
      header: 'Foto',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const photo = cell.getValue();
        return photo ? (
          <button onClick={() => setPhotoUrl(photo)} className="text-blue-600 hover:underline text-sm">
            Ver foto
          </button>
        ) : (
          <span className="text-gray-500">Sin foto</span>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => {
        const status = cell.getValue();
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(status)}`}>
            {getStatusLabel(status)}
          </span>
        );
      }
    },
    {
      accessorKey: 'rejectionReason',
      header: 'Razón del Rechazo',
      enableColumnFilter: false,
      cell: ({row}) => {
        const report = row.original;
        return report.status === 'rejected' ? (report.rejectionReason || '-') : '-';
      }
    }
  ]), []);

  const reportsDataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/ConsignmentReport`, {
      records,
      currentPage,
      order,
      search,
      filter,
      attributes: ['id', 'photo', 'amount', 'reference', 'balanceType', 'status', 'rejectionReason', 'createdAt'],
      searchAttributes: ['reference']
    });
  }, [appDataStore]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return "Aprobado";
      case "rejected":
        return "Rechazado";
      case "pending":
        return "Pendiente";
      default:
        return status;
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setReportFormData(prev => ({
          ...prev,
          photo: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReportSubmit = async () => {
    try {
      if (!reportFormData.amount || !reportFormData.reference || !reportFormData.balanceType) {
        alert('Por favor completa todos los campos requeridos');
        return;
      }

      setReportingLoading(true);
      const data = await appDataStore.post("/consignment-report/create", {
        photo: reportFormData.photo,
        amount: parseFloat(reportFormData.amount),
        reference: reportFormData.reference,
        balanceType: reportFormData.balanceType
      });

      alert('Consignación reportada exitosamente');
      setShowReportModal(false);
      setReportFormData({
        photo: null,
        amount: '',
        reference: '',
        balanceType: ''
      });
      setHistoryRefreshKey(prev => prev + 1);
      setReportsRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al reportar consignación:", err);
      alert("Error al reportar consignación: " + err.message);
    } finally {
      setReportingLoading(false);
    }
  };

  return (
    <>
      <div className="text-black">
        {/* Sección de Saldos Actuales */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Saldos Actuales</h3>

          {loading && balances.length === 0 ? (
            <div className="text-center py-8 text-gray-600">Cargando saldos...</div>
          ) : balances.length === 0 ? (
            <div className="text-center py-8 text-gray-600">No hay saldos disponibles</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {balances.map((balance) => (
                <div
                  key={balance.id}
                  className="border border-gray-300 rounded-lg p-4 bg-gradient-to-br from-blue-50 to-blue-100"
                >
                  <p className="text-sm text-gray-600 mb-2">Tipo de Saldo</p>
                  <p className="text-lg font-semibold text-gray-800 mb-3">{balance.type}</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatters.cash(balance.balance)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sección de Historial y Reportes */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Movimientos</h3>
            <button
              onClick={() => setShowReportModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reportar Consignación
            </button>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b border-gray-300 mb-6 gap-0">
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-3 font-medium transition-all border-b-2 ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Historial de recargas de saldo
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-4 py-3 font-medium transition-all border-b-2 ${
                activeTab === 'reports'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Reporte de consignaciones
            </button>
          </div>

          {/* Tab Content - Historial de Recargas */}
          {activeTab === 'history' && (
            <DataTable
              exportFileName={`historial_recargas_${moment().format('DDMMYY_HHmm')}`}
              columns={historyColumns}
              dataFetch={historyDataFetch}
              defaultSorting={[{id: 'createdAt', desc: true}]}
              refreshDependencies={historyRefreshKey}
            />
          )}

          {/* Tab Content - Mis Reportes de Consignación */}
          {activeTab === 'reports' && (
            <DataTable
              exportFileName={`mis_reportes_consignacion_${moment().format('DDMMYY_HHmm')}`}
              columns={reportsColumns}
              dataFetch={reportsDataFetch}
              defaultSorting={[{id: 'createdAt', desc: true}]}
              refreshDependencies={reportsRefreshKey}
            />
          )}
        </div>

        {/* Modal foto */}
        {photoUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setPhotoUrl(null)}>
            <div className="relative max-w-3xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
              <button onClick={() => setPhotoUrl(null)} className="absolute top-0 right-0 -mt-8 text-white text-2xl font-bold leading-none hover:text-gray-300">×</button>
              <img src={photoUrl} alt="Comprobante de consignación" className="max-w-full max-h-[85vh] rounded-lg shadow-xl object-contain" />
            </div>
          </div>
        )}

        {/* Modal Reportar Consignación */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Reportar Consignación</h3>

              <div className="space-y-4">
                {/* Campo de Foto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto de Comprobante
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {reportFormData.photo && (
                    <div className="mt-2">
                      <img
                        src={reportFormData.photo}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  )}
                </div>

                {/* Campo de Tipo de Saldo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Saldo *
                  </label>
                  <select
                    name="balanceType"
                    value={reportFormData.balanceType}
                    onChange={handleReportChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Selecciona un tipo de saldo</option>
                    {balances.map((balance) => (
                      <option key={balance.id} value={balance.type}>
                        {balance.type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Campo de Monto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={reportFormData.amount}
                    onChange={handleReportChange}
                    placeholder="Ej: 50000"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>

                {/* Campo de Referencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Referencia / Número de Transacción
                  </label>
                  <input
                    type="text"
                    name="reference"
                    value={reportFormData.reference}
                    onChange={handleReportChange}
                    placeholder="Ej: TRX12345678"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-2 mt-6">
                <button
                  onClick={handleReportSubmit}
                  disabled={reportingLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {reportingLoading ? 'Reportando...' : 'Reportar'}
                </button>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportFormData({ photo: null, amount: '', reference: '', balanceType: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});
